import { useState } from "react";
import { api } from "../lib/api.js";
import type { FindResult } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

interface Props {
  projectId: string;
  onClose: () => void;
  /** Called after a successful replace so the workspace can reload content. */
  onReplaced: () => void | Promise<void>;
}

/**
 * Project-wide find & replace across every manuscript scene. Find shows a
 * grouped, per-scene preview with context; replace is tag-aware on the server
 * (only text between tags is changed) and appends a revision per scene for undo.
 * Scenes can be individually excluded before replacing.
 */
export function FindReplace({ projectId, onClose, onReplaced }: Props) {
  const toast = useToast();
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [result, setResult] = useState<FindResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const runFind = async () => {
    const query = find.trim();
    if (!query) return;
    setLoading(true);
    setConfirming(false);
    try {
      const res = await api.findInProject(projectId, query, { caseSensitive, wholeWord });
      setResult(res);
      setExcluded(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const includedScenes = result
    ? result.matches.filter((m) => !excluded.has(m.sceneId))
    : [];
  const includedCount = includedScenes.reduce((n, m) => n + m.count, 0);

  const toggleScene = (sceneId: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
    setConfirming(false);
  };

  const runReplace = async () => {
    if (!result || includedScenes.length === 0) return;
    setReplacing(true);
    try {
      // If every matching scene is included, omit sceneIds (replace all).
      const allIncluded = includedScenes.length === result.matches.length;
      const res = await api.replaceInProject(projectId, {
        query: find.trim(),
        replacement: replace,
        caseSensitive,
        wholeWord,
        ...(allIncluded ? {} : { sceneIds: includedScenes.map((m) => m.sceneId) })
      });
      toast.success(
        `Replaced ${res.replacements} occurrence${res.replacements === 1 ? "" : "s"} across ${res.scenesChanged} scene${res.scenesChanged === 1 ? "" : "s"}.`
      );
      await onReplaced();
      // Re-run the search so the preview reflects the new state.
      setConfirming(false);
      await runFind();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Replace failed");
    } finally {
      setReplacing(false);
    }
  };

  const footer = (
    <div className="row gap-2" style={{ width: "100%", alignItems: "center" }}>
      <span className="muted" style={{ fontSize: 12 }}>
        {result
          ? result.totalMatches === 0
            ? "No matches."
            : `${includedCount} of ${result.totalMatches} match${result.totalMatches === 1 ? "" : "es"} in ${includedScenes.length} scene${includedScenes.length === 1 ? "" : "s"} selected`
          : "Enter a search term, then Find."}
      </span>
      <div className="spacer" />
      <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
      {confirming ? (
        <button className="btn btn-sm btn-danger" onClick={runReplace} disabled={replacing}>
          {replacing ? "Replacing…" : `Confirm — replace ${includedCount}`}
        </button>
      ) : (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setConfirming(true)}
          disabled={!result || includedScenes.length === 0 || replacing}
          title={replace === "" ? "Replacing with an empty string will delete the matched text" : undefined}
        >
          <Icon name="redo" size={14} /> Replace {includedCount > 0 ? includedCount : ""}
        </button>
      )}
    </div>
  );

  return (
    <Modal title="Find & Replace" subtitle="Across the whole manuscript" onClose={onClose} width={660} footer={footer}>
      <div className="fr-fields">
        <div className="fr-row">
          <Icon name="search" size={15} className="fr-row-icon" />
          <input
            className="input fr-input"
            placeholder="Find in manuscript…"
            value={find}
            autoFocus
            onChange={(e) => { setFind(e.target.value); setConfirming(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") void runFind(); }}
          />
          <button className="btn btn-sm btn-primary" onClick={() => void runFind()} disabled={loading || !find.trim()}>
            {loading ? "Finding…" : "Find"}
          </button>
        </div>
        <div className="fr-row">
          <Icon name="edit" size={15} className="fr-row-icon" />
          <input
            className="input fr-input"
            placeholder="Replace with… (leave empty to delete the text)"
            value={replace}
            onChange={(e) => { setReplace(e.target.value); setConfirming(false); }}
          />
        </div>
        <div className="fr-opts">
          <label className="fr-check">
            <input type="checkbox" checked={caseSensitive} onChange={(e) => { setCaseSensitive(e.target.checked); setConfirming(false); }} />
            Match case
          </label>
          <label className="fr-check">
            <input type="checkbox" checked={wholeWord} onChange={(e) => { setWholeWord(e.target.checked); setConfirming(false); }} />
            Whole word
          </label>
        </div>
      </div>

      <div className="fr-results">
        {!result && !loading && (
          <div className="fr-hint">
            Find every occurrence across all scenes, review the matches in context, then replace —
            individually excluding any scene you want to keep. Each changed scene keeps a revision you can restore.
          </div>
        )}
        {result && result.totalMatches === 0 && (
          <div className="fr-hint">No matches for “{result.query}”.</div>
        )}
        {result && result.matches.map((m) => {
          const isExcluded = excluded.has(m.sceneId);
          return (
            <div key={m.sceneId} className={`fr-scene ${isExcluded ? "fr-scene--off" : ""}`}>
              <label className="fr-scene-head">
                <input type="checkbox" checked={!isExcluded} onChange={() => toggleScene(m.sceneId)} />
                <span className="strong truncate">{m.sceneTitle}</span>
                {m.chapterTitle && <span className="fr-chapter">{m.chapterTitle}</span>}
                <span className="spacer" />
                <span className="fr-count">{m.count}</span>
              </label>
              <div className="fr-snippets">
                {m.snippets.map((s, i) => (
                  <div key={i} className="fr-snippet">
                    <span className="faint">…{s.before}</span>
                    <mark className="fr-mark">{s.match}</mark>
                    {replace !== "" && <mark className="fr-mark fr-mark--new">{replace}</mark>}
                    <span className="faint">{s.after}…</span>
                  </div>
                ))}
                {m.count > m.snippets.length && (
                  <div className="faint fr-more">+{m.count - m.snippets.length} more in this scene</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
